'use client'

import type { CatalogSubchannelView } from '@/lib/connectors/get-catalog'
import { Switch } from '@/components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { subchannelFaviconUrl } from '@/lib/connectors/subchannel-favicon'
import { cn } from '@/lib/utils'

/** White-label overrides: frontend-only rebrand without touching DB data. */
const SUBCHANNEL_DISPLAY_OVERRIDE: Record<string, { name?: string; description?: string }> = {
  sonobi: {
    name: 'Atrium Performance Network',
    description: 'Atrium’s in-house SSP for programmatic and streaming ad delivery at scale.',
  },
}

export interface SubchannelCardProps {
  sub: CatalogSubchannelView
  channelKey: string
  onOpen?: (sub: CatalogSubchannelView) => void
}

const CHANNEL_CARD_META: Record<string, { bg: string; logo: string }> = {
  search: {
    bg: 'bg-ch-search',
    logo: 'bg-[image:var(--tile-search)]',
  },
  social: {
    bg: 'bg-ch-social',
    logo: 'bg-[image:var(--tile-social)]',
  },
  tv: {
    bg: 'bg-ch-tv',
    logo: 'bg-[image:var(--tile-tv)]',
  },
  programmatic: {
    bg: 'bg-ch-programmatic',
    logo: 'bg-[image:var(--tile-programmatic)]',
  },
  audio: {
    bg: 'bg-ch-audio',
    logo: 'bg-[image:var(--tile-audio)]',
  },
  retail: {
    bg: 'bg-ch-retail',
    logo: 'bg-[image:var(--tile-retail)]',
  },
}

function isLive(sub: CatalogSubchannelView): boolean {
  return (
    sub.connection?.status === 'connected' || sub.connection?.status === 'pending'
  )
}

function connectorLogo(sub: CatalogSubchannelView): string {
  return (
    sub.logo_text ||
    sub.name.split(/\s+/)[0]?.[0]?.toUpperCase() ||
    '?'
  )
}

function connectionLabel(sub: CatalogSubchannelView): string {
  const conn = sub.connection
  if (!conn) return 'Connected'
  return (
    conn.display_account_name ||
    (conn.auth_mode === 'atrium_managed' ? 'Atrium managed' : sub.name)
  )
}

function connectionTooltip(sub: CatalogSubchannelView): string {
  const conn = sub.connection
  const label = connectionLabel(sub)
  if (!conn) return label

  const details = [label]
  if (conn.auth_mode === 'atrium_managed') details.push('Connected via Atrium')
  if (conn.connected_at) details.push(`Connected ${formatDate(conn.connected_at)}`)
  if (conn.last_synced_at) details.push(`Synced ${formatDate(conn.last_synced_at)}`)
  return details.join(' · ')
}

function formatDate(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function SubchannelCard({
  sub,
  channelKey,
  onOpen,
}: SubchannelCardProps) {
  if (sub.status === 'deprecated') return null

  const meta = CHANNEL_CARD_META[channelKey] ?? {
    bg: 'bg-brand',
    logo: 'bg-[image:var(--tile-brand)]',
  }
  const override = SUBCHANNEL_DISPLAY_OVERRIDE[sub.key]
  const displayName = override?.name ?? sub.name
  const displayDescription = override?.description ?? sub.description
  const live = isLive(sub)
  const comingSoon = sub.status === 'coming_soon'
  const favicon = subchannelFaviconUrl(sub.key, sub.logo_url)
  const connectedLabel = connectionLabel(sub)
  const openCard = () => {
    if (comingSoon) return
    onOpen?.(sub)
  }

  if (comingSoon) {
    return (
      <div
        aria-disabled="true"
        className={cn(
          'flex min-h-32 w-full cursor-not-allowed flex-col gap-[9px] rounded-card p-3.5 text-left text-white opacity-55',
          meta.bg,
        )}
      >
        <div className="flex items-center gap-2.5">
          {favicon ? (
            <div className="flex size-9 flex-none items-center justify-center overflow-hidden rounded-[10px] bg-white">
              <img
                src={favicon}
                alt=""
                loading="lazy"
                className="size-[22px] object-contain"
              />
            </div>
          ) : (
            <div className={cn('flex size-9 flex-none items-center justify-center rounded-[10px] text-[13px] font-extrabold text-white', meta.logo)}>
              {connectorLogo(sub)}
            </div>
          )}
          <div className="text-sm font-bold">{displayName}</div>
        </div>
        <div className="flex-1 text-[11.5px] leading-[1.35] text-white/75">
          {displayDescription || `${displayName} connector.`}
        </div>
        <div className="flex items-center justify-between">
          <span className="rounded-full border border-white/30 bg-white/15 px-[9px] py-[3px] text-[11px] font-semibold text-white/85">
            Coming soon
          </span>
        </div>
      </div>
    )
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openCard}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          openCard()
        }
      }}
      className={cn(
        'flex min-h-32 w-full cursor-pointer flex-col gap-[9px] rounded-card p-3.5 text-left text-white outline-none focus-visible:ring-2 focus-visible:ring-white/60',
        meta.bg,
      )}
    >
      <div className="flex items-center gap-2.5">
        {favicon ? (
          <div className="flex size-9 flex-none items-center justify-center overflow-hidden rounded-[10px] bg-white">
            <img
              src={favicon}
              alt=""
              loading="lazy"
              className="size-[22px] object-contain"
            />
          </div>
        ) : (
          <div className={cn('flex size-9 flex-none items-center justify-center rounded-[10px] text-[13px] font-extrabold text-white', meta.logo)}>
            {connectorLogo(sub)}
          </div>
        )}
        <div className="text-sm font-bold">{displayName}</div>
      </div>
      <div className="flex-1 text-[11.5px] leading-[1.35] text-white/75">
        {displayDescription || `${displayName} connector.`}
      </div>
      <div className="flex items-center justify-between">
        {live ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex max-w-full items-center gap-[5px] rounded-full bg-white/20 px-[9px] py-[3px] text-[11px] font-semibold text-white">
                  <span className="size-1.5 shrink-0 rounded-full bg-white shadow-[0_0_0_3px_rgba(255,255,255,0.25)]" />
                  <span className="truncate">{connectedLabel}</span>
                </span>
              </TooltipTrigger>
              <TooltipContent>{connectionTooltip(sub)}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <>
            <span className="font-mono text-[11px] text-white/70">Not connected</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    <Switch
                      checked={false}
                      aria-label={`Enable ${displayName}`}
                      onCheckedChange={(checked) => {
                        if (checked) onOpen?.(sub)
                      }}
                      className="data-[state=checked]:bg-white data-[state=unchecked]:bg-white/30 [&_[data-slot=switch-thumb]]:bg-white"
                    />
                  </span>
                </TooltipTrigger>
                <TooltipContent>{`Enable ${displayName}`}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </>
        )}
      </div>
    </div>
  )
}
